import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import fetchWithCredentials from "apiClient";
import { Manager } from "types/Manager";

interface ManagersContextType {
  managers: Manager[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  managersCount: number;
  selectedContactId: number | null;
  setSelectedContactId: (contactId: number | null) => void;
}

const ManagersContext = createContext<ManagersContextType | undefined>(
  undefined
);

export function ManagersProvider({ children }: { children: ReactNode }) {
  const [managers, setManagers] = useState<Manager[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null
  );

  const fetchManagers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const managers = await fetchWithCredentials("/manager-api/managers/");
      setManagers(managers);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching managers");
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchManagers();
  }, [fetchManagers]);

  const managersCount = useMemo(() => managers.length, [managers]);

  return (
    <ManagersContext.Provider
      value={{
        managers,
        loading,
        error,
        refresh,
        managersCount,
        selectedContactId,
        setSelectedContactId,
      }}
    >
      {children}
    </ManagersContext.Provider>
  );
}

export function useManagers() {
  const context = useContext(ManagersContext);
  if (!context) {
    throw new Error("useManagers must be used within a ManagersProvider");
  }
  return context;
}
